import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, FileText, DownloadSimple, Plus, Trash, X, PaperPlaneTilt, CurrencyInr, UploadSimple, Sparkle } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { API } from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import SendGmailModal from "../../components/SendGmailModal";
import PaymentModal, { PaymentStatusPill } from "../../components/PaymentModal";

const KINDS = [
  { k: "quotation",   label: "Quotation",        Icon: FileText, accent: "#0A0A0A" },
  { k: "proforma",    label: "Proforma Invoice", Icon: Receipt,  accent: "#F4A261" },
  { k: "tax_invoice", label: "Tax Invoice",      Icon: Receipt,  accent: "#D90429" },
];

const today = () => new Date().toISOString().slice(0,10);
const mkItem = () => ({ _uid: `it_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, description:"", hsn:"", quantity:1, unit:"Unit", unit_price:0, discount_percent:0, gst_percent:18 });
const emptyForm = {
  kind: "quotation", number: "", issue_date: today(), valid_until: "",
  client_name:"", client_company:"", client_address:"", client_gstin:"", client_email:"", client_phone:"",
  place_of_supply:"Delhi", is_igst:false,
  items: [mkItem()],
  notes:"", terms:"50% advance, balance against delivery. Prices valid for 30 days.",
};

export default function Billing() {
  const { active, activeId, companies } = useCompany();
  const [tab, setTab] = useState("quotation");
  const [docs, setDocs] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [legacyPicked, setLegacyPicked] = useState({});
  const [legacyTarget, setLegacyTarget] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({...emptyForm});
  const [saving, setSaving] = useState(false);
  const [autoDownload, setAutoDownload] = useState(false);
  const [sendTarget, setSendTarget] = useState(null); // billing doc to email
  const [payTarget, setPayTarget] = useState(null); // billing doc to record payment
  const [paySummaries, setPaySummaries] = useState({}); // id → {status, outstanding}
  const [extractOpen, setExtractOpen] = useState(false);
  const [extractFile, setExtractFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractMeta, setExtractMeta] = useState(null); // {source_number, source_total, pages}

  const load = async () => {
    if (!activeId) { setDocs([]); return; }
    const { data } = await api.get(`/finance/billing/docs`, { params: { kind: tab, company_id: activeId } });
    setDocs(data);
    // Fetch payment summaries in parallel
    const summaries = {};
    await Promise.all((data || []).map(async (d) => {
      try {
        const { data: s } = await api.get("/finance/payments", { params: { parent_type: "invoice", parent_id: d.id } });
        summaries[d.id] = s;
      } catch { /* ignore */ }
    }));
    setPaySummaries(summaries);
    // Also check for legacy unassigned invoices
    try {
      const { data: legacy } = await api.get("/finance/billing/docs/unassigned");
      setUnassigned(legacy || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); setLegacyTarget(activeId || ""); /* eslint-disable-next-line */ }, [tab, activeId]);

  const bulkMoveLegacy = async () => {
    const ids = Object.entries(legacyPicked).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error("Pick at least one invoice"); return; }
    if (!legacyTarget) { toast.error("Choose a target company"); return; }
    try {
      const { data } = await api.post("/finance/billing/docs/bulk-move", { ids, company_id: legacyTarget });
      toast.success(`Moved ${data.moved} invoice(s)`);
      setLegacyPicked({});
      setLegacyOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
  };

  const totals = (() => {
    let sub=0, disc=0, tax=0, gst=0;
    form.items.forEach(it => {
      const base = (Number(it.quantity)||0) * (Number(it.unit_price)||0);
      const d = base * (Number(it.discount_percent)||0)/100;
      const t = base - d;
      sub += base; disc += d; tax += t;
      gst += t * (Number(it.gst_percent)||0)/100;
    });
    return { sub, disc, tax, gst, grand: tax + gst };
  })();

  const addItem = () => setForm(f => ({...f, items:[...f.items, mkItem()]}));
  const removeItem = (i) => setForm(f => ({...f, items: f.items.filter((_,idx)=>idx!==i)}));
  const updateItem = (i, k, v) => setForm(f => ({...f, items: f.items.map((it,idx)=>idx===i?{...it,[k]:v}:it)}));

  const save = async () => {
    if (!activeId) { toast.error("Pick a company first (top-right selector)"); return; }
    if (!form.client_name.trim()) { toast.error("Client name required"); return; }
    if (form.items.length === 0 || !form.items.some(it => it.description.trim())) { toast.error("At least one item required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: activeId,
        items: form.items.filter(it => it.description.trim()).map(it => ({
          ...it,
          quantity: Number(it.quantity)||0,
          unit_price: Number(it.unit_price)||0,
          discount_percent: Number(it.discount_percent)||0,
          gst_percent: Number(it.gst_percent)||0,
        })),
      };
      const { data } = await api.post("/finance/billing/docs", payload);
      toast.success(`${data.number} created for ${active?.name || "company"}`);
      const autoOuts = Array.isArray(data.auto_stock_outs) ? data.auto_stock_outs : [];
      if (autoOuts.length) {
        const totalQty = autoOuts.reduce((s, x) => s + (Number(x.qty) || 0), 0);
        toast.success(`Inventory auto-decremented · ${autoOuts.length} item(s), ${totalQty} unit(s) issued`, { duration: 6000 });
      }
      setOpen(false); setForm({...emptyForm, kind: tab});
      load();
      // Auto-download only if the user explicitly ticked the option.
      if (autoDownload) {
        setTimeout(() => window.open(`${API}/finance/billing/docs/${data.id}/pdf`, "_blank"), 400);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.number}?`)) return;
    await api.delete(`/finance/billing/docs/${d.id}`);
    setDocs((curr) => curr.filter((x) => x.id !== d.id));
    toast.success("Deleted");
  };

  const openNew = (kind) => { setForm({...emptyForm, kind}); setExtractMeta(null); setOpen(true); };
  const isTax = form.kind === "tax_invoice";

  const handleExtract = async () => {
    if (!extractFile) { toast.error("Choose a PDF first"); return; }
    if (!/\.pdf$/i.test(extractFile.name)) { toast.error("Only PDF supported"); return; }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", extractFile);
      const { data } = await api.post("/finance/billing/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      // Map AI output → form (respect current tab: quotation / proforma / tax_invoice)
      const targetKind = tab;
      const items = (data.items || []).map((it) => ({
        ...mkItem(),
        description: it.description || "",
        hsn: it.hsn || "",
        quantity: Number(it.quantity) || 1,
        unit: it.unit || "Unit",
        unit_price: Number(it.unit_price) || 0,
        discount_percent: Number(it.discount_percent) || 0,
        gst_percent: Number(it.gst_percent) || 18,
      }));
      setForm({
        ...emptyForm,
        kind: targetKind,
        number: data.number || "",   // populate the extracted invoice # so user can override
        issue_date: data.issue_date || today(),
        valid_until: data.valid_until || "",
        client_name: data.client_name || "",
        client_company: data.client_company || "",
        client_address: data.client_address || "",
        client_gstin: data.client_gstin || "",
        client_email: data.client_email || "",
        client_phone: data.client_phone || "",
        place_of_supply: data.place_of_supply || "Delhi",
        is_igst: !!data.is_igst,
        items: items.length ? items : [mkItem()],
        notes: data.notes || "",
        terms: data.terms || emptyForm.terms,
      });
      setExtractMeta(data._meta || null);
      setExtractOpen(false);
      setExtractFile(null);
      setOpen(true);
      toast.success(`Parsed ${(data._meta?.pages) || 1} page(s) into ${KINDS.find(x=>x.k===targetKind)?.label || "document"} · review & save`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally { setExtracting(false); }
  };

  return (
    <Layout title="Billing" subtitle={`${active?.name || "—"} · Quotations · Proforma · Tax Invoices · Shelves Tech branded PDFs`}>
      {unassigned.length > 0 && (
        <div className="mb-4 border border-[#F4A261] bg-[#F4A261]/10 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="legacy-banner">
          <span className="text-sm">
            <b className="text-[#0A0A0A]">{unassigned.length} legacy invoice{unassigned.length === 1 ? "" : "s"}</b> from before company scoping — not shown here until you assign each to the right company.
          </span>
          <button onClick={() => setLegacyOpen(true)} className="ml-auto text-xs px-3 py-1.5 bg-[#0A0A0A] text-white font-semibold hover:bg-[#D90429]" data-testid="legacy-review-btn">Review &amp; assign</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-6">
        {KINDS.map(({k, label, Icon, accent}) => (
          <button key={k} onClick={()=>setTab(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border ${tab===k?"text-white border-transparent":"border-black/15 hover:border-[#0A0A0A] text-[#0A0A0A]"}`}
            style={tab===k?{backgroundColor:accent}:{}}>
            <Icon size={16} weight="bold"/> {label}
          </button>
        ))}
        <div className="flex-1"/>
        <button
          onClick={() => { setExtractFile(null); setExtractOpen(true); }}
          data-testid="auto-fetch-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white"
        >
          <Sparkle size={16} weight="bold"/> Auto-Fetch from PDF
        </button>
        <button onClick={()=>openNew(tab)} data-testid="new-doc-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">
          <Plus size={16} weight="bold"/> New {KINDS.find(x=>x.k===tab)?.label}
        </button>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="billing-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>{["Number","Client","Date","Items","Grand Total",""].map(h => <th key={h} className="px-4 py-3 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {docs.length===0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-[#717680]">No {KINDS.find(x=>x.k===tab)?.label.toLowerCase()}s yet. Click &ldquo;New&rdquo; to create one.</td></tr>}
            {docs.map(d => (
              <tr key={d.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`doc-row-${d.id}`}>
                <td className="px-4 py-3 font-mono-shelv text-[#D90429] font-bold">{d.number}</td>
                <td className="px-4 py-3"><div className="font-semibold">{d.client_name}</div><div className="text-[10px] text-[#717680]">{d.client_company}</div></td>
                <td className="px-4 py-3 text-[#4A4D57] font-mono-shelv text-xs">{d.issue_date}</td>
                <td className="px-4 py-3 text-[#4A4D57]">{d.items?.length || 0}</td>
                <td className="px-4 py-3 font-mono-shelv font-bold">₹{Number(d.grand_total).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {paySummaries[d.id] && <span className="mr-2"><PaymentStatusPill status={paySummaries[d.id].status} /></span>}
                  <button onClick={() => setPayTarget(d)} className="inline-flex items-center gap-1 px-2 py-1 border border-[#0D9488] text-[#0D9488] text-[11px] font-bold hover:bg-[#0D9488] hover:text-white mr-1" data-testid={`pay-${d.id}`}><CurrencyInr size={12} weight="bold"/> Pay</button>
                  <button onClick={() => setSendTarget(d)} className="inline-flex items-center gap-1 px-2 py-1 border border-[#D90429] text-[#D90429] text-[11px] font-bold hover:bg-[#D90429] hover:text-white mr-1" data-testid={`send-gmail-${d.id}`}><PaperPlaneTilt size={12} weight="bold"/> Email</button>
                  <a href={`${API}/finance/billing/docs/${d.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-[#0A0A0A] text-white text-[11px] font-bold hover:bg-[#D90429] mr-1" data-testid={`pdf-${d.id}`}><DownloadSimple size={12} weight="bold"/> PDF</a>
                  <button onClick={()=>remove(d)} className="p-1.5 text-[#D90429] hover:bg-[#D90429]/10" data-testid={`del-${d.id}`}><Trash size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl rounded-none max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">New {KINDS.find(x=>x.k===form.kind)?.label}</DialogTitle></DialogHeader>

          {extractMeta && (
            <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 px-3 py-2 text-xs flex flex-wrap items-center gap-3" data-testid="extract-meta-banner">
              <Sparkle size={14} weight="bold" className="text-[#0D9488]"/>
              <span className="text-[#0A0A0A] font-semibold">Auto-fetched from PDF</span>
              {extractMeta.source_number && <span className="font-mono-shelv text-[#4A4D57]">Source #: <b>{extractMeta.source_number}</b></span>}
              {extractMeta.source_total > 0 && <span className="font-mono-shelv text-[#4A4D57]">Source total: <b>₹{Number(extractMeta.source_total).toLocaleString("en-IN")}</b></span>}
              {extractMeta.pages > 0 && <span className="font-mono-shelv text-[#4A4D57]">{extractMeta.pages} page(s)</span>}
              <span className="text-[#717680]">· Review fields below before saving</span>
            </div>
          )}

          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2"><Label>Client Name *</Label><Input value={form.client_name} onChange={e=>setForm({...form,client_name:e.target.value})} className="rounded-none" data-testid="client-name-input"/></div>
            <div className="col-span-2"><Label>Client Company</Label><Input value={form.client_company} onChange={e=>setForm({...form,client_company:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.client_address} onChange={e=>setForm({...form,client_address:e.target.value})} className="rounded-none"/></div>
            <div><Label>Client GSTIN</Label><Input value={form.client_gstin} onChange={e=>setForm({...form,client_gstin:e.target.value})} className="rounded-none"/></div>
            <div><Label>Place of Supply</Label><Input value={form.place_of_supply} onChange={e=>setForm({...form,place_of_supply:e.target.value})} className="rounded-none"/></div>
            <div><Label>Phone</Label><Input value={form.client_phone} onChange={e=>setForm({...form,client_phone:e.target.value})} className="rounded-none"/></div>
            <div><Label>Email</Label><Input value={form.client_email} onChange={e=>setForm({...form,client_email:e.target.value})} className="rounded-none"/></div>
            <div><Label>Issue Date</Label><Input type="date" value={form.issue_date} onChange={e=>setForm({...form,issue_date:e.target.value})} className="rounded-none"/></div>
            <div><Label>Valid Until</Label><Input type="date" value={form.valid_until} onChange={e=>setForm({...form,valid_until:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2">
              <Label className="flex items-center gap-2">
                Document Number
                <span className="text-[10px] text-[#717680] font-normal italic">optional · leave blank for auto-generated</span>
              </Label>
              <Input value={form.number} onChange={e=>setForm({...form,number:e.target.value})} placeholder="e.g. INV-2026-042 · overrides auto-generated" className="rounded-none font-mono-shelv" data-testid="doc-number-input"/>
            </div>
            <div className="col-span-2 flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="igst-toggle">
                <input type="checkbox" checked={form.is_igst} onChange={e=>setForm({...form,is_igst:e.target.checked})} className="accent-[#D90429] w-4 h-4"/>
                Inter-state supply (IGST instead of CGST+SGST)
              </label>
            </div>
          </div>

          <div className="border-t border-black/10 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Line Items</div>
              <button onClick={addItem} className="text-xs px-2 py-1 border border-black/15 hover:bg-[#0A0A0A] hover:text-white" data-testid="add-item-button"><Plus size={12} weight="bold" className="inline mr-1"/>Add row</button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={it._uid || idx} className="border border-black/5 bg-[#FAFBFC] p-2" data-testid={`item-${idx}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <Input placeholder="Description *" value={it.description} onChange={e=>updateItem(idx,"description",e.target.value)} className={`rounded-none h-9 ${isTax?"col-span-3":"col-span-5"}`}/>
                    {isTax && <Input placeholder="HSN" value={it.hsn} onChange={e=>updateItem(idx,"hsn",e.target.value)} className="rounded-none h-9 col-span-1"/>}
                    <Input placeholder="Qty" type="number" value={it.quantity} onChange={e=>updateItem(idx,"quantity",e.target.value)} className="rounded-none h-9 col-span-1"/>
                    <Input placeholder="Unit" value={it.unit} onChange={e=>updateItem(idx,"unit",e.target.value)} className="rounded-none h-9 col-span-1"/>
                    <Input placeholder="Rate ₹" type="number" value={it.unit_price} onChange={e=>updateItem(idx,"unit_price",e.target.value)} className="rounded-none h-9 col-span-2"/>
                    <Input placeholder="Disc %" type="number" value={it.discount_percent} onChange={e=>updateItem(idx,"discount_percent",e.target.value)} className="rounded-none h-9 col-span-1"/>
                    {isTax && <Input placeholder="GST %" type="number" value={it.gst_percent} onChange={e=>updateItem(idx,"gst_percent",e.target.value)} className="rounded-none h-9 col-span-1"/>}
                    <div className="col-span-1 text-right text-xs font-mono-shelv pt-2">₹{((Number(it.quantity)||0)*(Number(it.unit_price)||0)*(1-(Number(it.discount_percent)||0)/100)).toLocaleString("en-IN")}</div>
                    <button onClick={()=>removeItem(idx)} className="col-span-1 p-2 text-[#D90429] hover:bg-[#D90429]/10" data-testid={`remove-item-${idx}`}><X size={14}/></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals preview */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="col-span-1 text-xs text-[#717680] font-mono-shelv">
              ★ PDF will include: Shelves Tech watermark · Delivering Better Healthcare brand strip · ISO 9001:2015 + ISO 13485:2016 certifications footer
            </div>
            <div className="col-span-1 bg-[#FAFBFC] border border-black/10 p-3 text-sm">
              <div className="flex justify-between"><span className="text-[#717680]">Subtotal</span><span className="font-mono-shelv">₹{totals.sub.toLocaleString("en-IN",{maximumFractionDigits:2})}</span></div>
              <div className="flex justify-between"><span className="text-[#717680]">Discount</span><span className="font-mono-shelv">-₹{totals.disc.toLocaleString("en-IN",{maximumFractionDigits:2})}</span></div>
              <div className="flex justify-between"><span className="text-[#717680]">Taxable</span><span className="font-mono-shelv">₹{totals.tax.toLocaleString("en-IN",{maximumFractionDigits:2})}</span></div>
              {isTax && <div className="flex justify-between"><span className="text-[#717680]">{form.is_igst?"IGST":"CGST + SGST"}</span><span className="font-mono-shelv">₹{totals.gst.toLocaleString("en-IN",{maximumFractionDigits:2})}</span></div>}
              <div className="flex justify-between border-t border-[#D90429]/30 mt-1 pt-1 font-bold"><span>Grand Total</span><span className="font-mono-shelv text-[#D90429]">₹{(isTax?totals.grand:totals.tax).toLocaleString("en-IN",{maximumFractionDigits:2})}</span></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} className="rounded-none"/></div>
            <div><Label>Terms &amp; Conditions</Label><Textarea rows={2} value={form.terms} onChange={e=>setForm({...form,terms:e.target.value})} className="rounded-none"/></div>
          </div>

          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-doc-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">{saving?"Saving…":(autoDownload ? "Save & Download PDF" : "Save")}</button>
            <label className="inline-flex items-center gap-1.5 text-xs text-[#717680] cursor-pointer" data-testid="auto-download-toggle-label">
              <input type="checkbox" checked={autoDownload} onChange={(e) => setAutoDownload(e.target.checked)} data-testid="auto-download-toggle" className="accent-[#D90429]" />
              Auto-download PDF after save
            </label>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendGmailModal
        open={!!sendTarget}
        onOpenChange={(o) => !o && setSendTarget(null)}
        endpoint={sendTarget ? `/oauth/gmail/send/billing/${sendTarget.id}` : ""}
        defaultTo={sendTarget?.client_email || ""}
        defaultSubject={sendTarget ? `${(KINDS.find(k => k.k === sendTarget.kind)?.label) || "Document"} ${sendTarget.number} · Shelvestech` : ""}
        defaultBody={sendTarget ? `Dear ${sendTarget.client_name || "Sir/Madam"},

Please find attached the ${(KINDS.find(k => k.k === sendTarget.kind)?.label || "document").toLowerCase()} (${sendTarget.number}) for your reference, dated ${sendTarget.issue_date}.

Grand total: ₹${Number(sendTarget.grand_total || 0).toLocaleString("en-IN")}.

Kindly confirm receipt and revert with any queries.

Warm regards,` : ""}
        attachmentName={sendTarget ? `${(KINDS.find(k => k.k === sendTarget.kind)?.label || "Document").replace(/\s+/g, "_")}_${sendTarget.number}.pdf` : "document.pdf"}
        kindLabel={sendTarget ? (KINDS.find(k => k.k === sendTarget.kind)?.label || "Document") : "Document"}
        onSent={() => { toast.success("Email sent"); load(); }}
      />

      <PaymentModal
        open={!!payTarget}
        onOpenChange={(o) => !o && setPayTarget(null)}
        parentType="invoice"
        parentId={payTarget?.id}
        parentLabel={payTarget ? `${(KINDS.find(k => k.k === payTarget.kind)?.label) || "Invoice"} ${payTarget.number} · ${payTarget.client_name}` : ""}
        onUpdated={(s) => setPaySummaries((prev) => ({ ...prev, [payTarget.id]: s }))}
      />

      <Dialog open={extractOpen} onOpenChange={(o) => { if (!extracting) { setExtractOpen(o); if (!o) setExtractFile(null); } }}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Sparkle size={18} weight="bold" className="text-[#D90429]"/> Auto-Fetch Invoice from PDF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#4A4D57] leading-relaxed">
              Upload a PDF of an invoice already created in <b>Busy</b>, <b>Tally</b>, or any other accounting software.
              Our AI will read it and pre-fill a new <b>{KINDS.find(x=>x.k===tab)?.label || "document"}</b> in this system — you can review, edit and save.
            </p>
            <label
              htmlFor="extract-file-input"
              className="block border-2 border-dashed border-black/15 hover:border-[#D90429] p-6 text-center cursor-pointer transition-colors"
              data-testid="extract-drop-zone"
            >
              <UploadSimple size={28} weight="bold" className="mx-auto mb-2 text-[#717680]"/>
              <div className="text-sm font-semibold text-[#0A0A0A]">
                {extractFile ? extractFile.name : "Click to choose a PDF"}
              </div>
              <div className="text-[11px] text-[#717680] mt-1 font-mono-shelv uppercase tracking-widest">
                PDF only · Multi-page supported · Max ~10 MB
              </div>
              <input
                id="extract-file-input"
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => setExtractFile(e.target.files?.[0] || null)}
                data-testid="extract-file-input"
              />
            </label>
            {extracting && (
              <div className="flex items-center gap-2 text-xs text-[#0D9488] font-semibold">
                <span className="inline-block w-3 h-3 border-2 border-[#0D9488] border-t-transparent rounded-full animate-spin"/>
                Reading PDF with Gemini Vision… this may take 10–30 seconds
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => { setExtractOpen(false); setExtractFile(null); }}
              disabled={extracting}
              className="px-4 py-2 border border-black/15 text-sm disabled:opacity-50"
              data-testid="extract-cancel-button"
            >Cancel</button>
            <button
              onClick={handleExtract}
              disabled={!extractFile || extracting}
              className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="extract-submit-button"
            >
              <Sparkle size={14} weight="bold"/> {extracting ? "Parsing…" : "Parse & Pre-fill"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legacy invoice migration dialog */}
      <Dialog open={legacyOpen} onOpenChange={setLegacyOpen}>
        <DialogContent className="rounded-none max-w-3xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Assign legacy invoices to a company</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4A4D57] mb-3">
            These invoices were created before per-company scoping. Select the ones that belong to a company, choose the company, then click <b>Assign</b>.
            They&apos;ll only appear under that company&apos;s Billing view going forward.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs font-mono-shelv uppercase tracking-widest text-[#4A4D57]">Assign to</label>
            <select value={legacyTarget} onChange={(e) => setLegacyTarget(e.target.value)} className="border border-black/15 px-2 py-1 text-sm rounded-none" data-testid="legacy-target-select">
              <option value="">— choose company —</option>
              {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button onClick={() => {
              const all = {}; unassigned.forEach((d) => { all[d.id] = true; });
              setLegacyPicked(all);
            }} className="text-xs px-2 py-1 border border-black/15" data-testid="legacy-select-all">Select all</button>
            <button onClick={() => setLegacyPicked({})} className="text-xs px-2 py-1 border border-black/15" data-testid="legacy-clear-all">Clear</button>
          </div>
          <div className="border border-black/10 max-h-96 overflow-y-auto">
            <table className="w-full text-sm" data-testid="legacy-table">
              <thead className="bg-[#F5F6F8] text-left text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">
                <tr><th className="px-2 py-1.5 w-8"></th><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Number</th><th className="px-2 py-1.5">Client</th><th className="px-2 py-1.5 text-right">Grand total</th></tr>
              </thead>
              <tbody>
                {unassigned.map((d) => (
                  <tr key={d.id} className="border-t border-black/5" data-testid={`legacy-row-${d.id}`}>
                    <td className="px-2 py-1.5"><input type="checkbox" checked={!!legacyPicked[d.id]} onChange={(e) => setLegacyPicked({ ...legacyPicked, [d.id]: e.target.checked })} className="accent-[#D90429]"/></td>
                    <td className="px-2 py-1.5 font-mono-shelv text-[11px]">{d.issue_date}</td>
                    <td className="px-2 py-1.5 font-mono-shelv text-[11px]">{d.number}</td>
                    <td className="px-2 py-1.5">{d.client_name}</td>
                    <td className="px-2 py-1.5 font-mono-shelv text-right">₹{Number(d.grand_total || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <button onClick={() => setLegacyOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={bulkMoveLegacy} className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]" data-testid="legacy-move-btn">Assign to selected company</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
